<template>
  <BaseDialog centered contentClass="sync-opportunity" ref="dialog" :show-actions="true" persistent>
    <template slot="title"
      >{{ $t('page.opportunity-details.opportunity-manipulations.sync') }}
      <div class="sync-opportunity__subtitle">{{ $t('basic-words.to-2') }} {{ syncProfileName }}</div>
    </template>
    <template slot="content" v-if="opportunity">
      <div
        v-if="!searchSimilarOppPending && filteredSimilarOpportunities.length > 0"
        class="sync-opportunity__similitude-warning"
      >
        <div class="sync-opportunity__similitude-warning--title">
          <BaseIcon name="info" />
          {{ $tc('page.opportunity-details.sync-validation.similarity-warning', filteredSimilarOpportunities.length) }}
        </div>
        <div v-for="(opp, index) in filteredSimilarOpportunities" :key="index" class="sync-opportunity__similar-opp">
          {{ opp.name }}
        </div>
      </div>
      <form @click.prevent @keydown.enter.prevent>
        <div>
          <div class="sync-opportunity__rename-opportunity">
            <FormInput
              ref="opportunityName"
              :label="$t('page.opportunities.data-table-titles.title')"
              has-gray-background
              name="text"
              v-model="opportunityName"
              :readonly="!renamingOpportunity"
            />
            <BaseRoundButton
              :disabled="renamingOpportunity"
              icon="edit-2"
              is-white
              :tooltip-text="$t('actions.rename')"
              @click.native="onRenameOpportunity"
            >
            </BaseRoundButton>
          </div>
          <div class="sync-opportunity__rename-actions">
            <BaseButton v-if="renamingOpportunity" isSmall isOutlined @click.native="onCancelRenameOpportunity">
              {{ $t('actions.cancel') }}
            </BaseButton>
            <BaseButton v-if="renamingOpportunity" isSmall @click.native="renameOpportunity">
              {{ $t('actions.save') }}
            </BaseButton>
          </div>
          <FormInput
            ref="opportunityEmitter"
            :label="$t('page.opportunities.data-table-titles.emitter')"
            has-gray-background
            name="text"
            v-model="opportunity.emitter"
            readonly
          />
          <div class="sync-opportunity__external-fields" v-if="hasFieldMappings && displayableFieldMappings.length > 0">
            <div class="sync-opportunity__external-fields--subtitle">
              {{ $t('page.opportunity-details.sync-validation.sync-details') }}
            </div>
            <SyncFieldMappings
              :key="fieldMappingsKey"
              :field-mappings="opportunityFieldMappings"
              :values="fieldValues"
              :sync-profile-id="syncProfile.id"
              :sync-profile="syncProfile"
              :is-create="isCreate"
              :default-queries="defaultQueries"
              :create-defaults="createDefaults"
              @input="fieldValues = $event"
            />
          </div>
        </div>
        <div class="sync-opportunity__actions">
          <FormButton is-outlined class="sync-opportunity__cancel" @click.native="cancelClick">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit" :disabled="renamingOpportunity">
            <span>{{ $t('actions.confirm') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput.vue';
import SyncFieldMappings from '@/components/sync/SyncFieldMappings';

import {
  buildLookupFieldDefaults,
  filterFieldMappingsForMode,
  supportsFieldMappings,
  supportsSimilaritySearch,
} from '@/util/sync-profile-util';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'SyncOpportunityDialog',
  components: {
    BaseButton,
    BaseDialog,
    BaseIcon,
    BaseRoundButton,
    FormButton,
    FormInput,
    SyncFieldMappings,
  },
  data() {
    return {
      opportunity: null,
      opportunityName: null,
      oppSync: null,
      syncProfile: null,
      renamingOpportunity: false,
      fieldValues: {},
      defaultQueries: {},
      createDefaults: {},
      fieldMappingsKey: 0,
    };
  },
  computed: {
    ...mapGetters('opportunitySyncProfiles', ['opportunitySyncProfiles']),
    ...mapGetters('opportunitySyncProfile', { searchSimilarOppPending: 'pending' }),
    ...mapGetters('opportunitySyncProfile', ['similarOpportunities']),
    ...mapGetters('syncFieldMappings', { syncFieldMappingsList: 'fieldMappings', fieldMappingsPending: 'pending' }),
    isCreate() {
      return this.oppSync && this.oppSync.sync ? this.oppSync.sync.externalOpportunityId == null : true;
    },
    opportunityFieldMappings() {
      return [...this.syncFieldMappingsList].sort((a, b) => a.displayOrder - b.displayOrder);
    },
    displayableFieldMappings() {
      return filterFieldMappingsForMode(this.opportunityFieldMappings, this.isCreate);
    },
    hasFieldMappings() {
      return supportsFieldMappings(this.syncProfile.type);
    },
    filteredSimilarOpportunities() {
      return this.similarOpportunities.filter((opp) => opp.score > 0);
    },
    syncProfileName() {
      return this.syncProfile ? this.syncProfile.name : '';
    },
  },
  methods: {
    ...mapActions('opportunity', ['editOpportunity']),
    ...mapActions('opportunitySyncProfile', ['searchSimilarOpportunities']),
    ...mapActions('syncFieldMappings', ['getFieldMappings']),
    async openDialog(opportunity, oppSync) {
      this.opportunity = opportunity;
      this.opportunityName = opportunity.name;
      this.oppSync = oppSync;
      this.syncProfile = oppSync.profile;
      this.fieldValues = {};
      this.defaultQueries = {};
      this.createDefaults = {};
      this.fieldMappingsKey++;
      this.$refs.dialog.openModal();

      if (this.hasFieldMappings) {
        const fieldMappings = await this.getFieldMappings({
          syncProfileId: this.syncProfile.id,
          scope: 'opportunity',
          formOnly: true,
        });
        const { defaultQueries, createDefaults } = buildLookupFieldDefaults(fieldMappings, {
          accountQuery: this.opportunity.emitter || '',
          accountCreate: { name: this.opportunity.emitter || '' },
        });
        this.defaultQueries = defaultQueries;
        this.createDefaults = createDefaults;
        if (supportsSimilaritySearch(this.syncProfile.type)) {
          this.searchSimilarOpportunities({
            syncProfileId: this.syncProfile.id,
            searchQuery: this.opportunity.name,
          }).catch(() => {});
        }
      }
    },
    onCancelRenameOpportunity() {
      this.opportunityName = this.opportunity.name;
      this.renamingOpportunity = false;
    },
    onRenameOpportunity() {
      this.renamingOpportunity = true;
    },
    async renameOpportunity() {
      if (this.opportunityName !== this.opportunity.name) {
        let body = {
          name: this.opportunityName,
          evaluationGridTemplateId: null,
          statusId: this.opportunity.status ? this.opportunity.status.id : null,
          startDate: this.opportunity.startDate,
          endDate: this.opportunity.endDate,
          source: this.opportunity.source,
          emitter: this.opportunity.emitter,
          notes: this.opportunity.notes,
          version: this.opportunity.version,
        };
        await this.editOpportunity({ opportunityId: this.opportunity.id, opportunity: body });
      }
      this.renamingOpportunity = false;
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.renamingOpportunity = false;
    },
    onSubmit() {
      this.$emit('confirm', this.oppSync, this.fieldValues);
      this.closeDialog();
    },
    cancelClick() {
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__sync-opportunity {
  height: auto;
  width: 750px;
  position: relative;

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.sync-opportunity {
  &__subtitle {
    font-size: 16px;
    font-family: 'Lato';
    color: $color-normal-pale;
    font-weight: normal;
    text-transform: capitalize;
  }

  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__rename-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding-right: 48px;
  }

  &__cancel {
    margin-right: 12px;
  }

  &__similitude-warning {
    padding: 4px 8px;
    background: $color-orange-100;
    border-left: 5px solid $color-orange-400;
    margin-bottom: 16px;

    &--title {
      font-size: 16px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 4px;

      .base-icon {
        width: 16px;
        height: 16px;
      }
    }
  }

  &__similar-opp {
    font-size: 14px;
    background: $color-orange-100;
    border-radius: 3px;
    padding: 4px 8px;
    margin-bottom: 4px;
  }

  &__rename-opportunity {
    display: flex;
    align-items: center;
    gap: 8px;

    .base-icon {
      width: 16px;
      height: 16px;
    }
  }

  &__external-fields {
    border: 1px solid $color-border;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    padding: 16px;
    margin-top: 16px;

    &--subtitle {
      text-transform: uppercase;
      font-size: 14px;
      margin-bottom: 12px;
    }
  }
}
</style>
